import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission, Role } from '@sarha/shared';
import { apiGet, apiPost, setAccessToken } from './api';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  /** مطلوب منه تغيير كلمة المرور قبل استخدام النظام */
  mustChangePassword?: boolean;
}

interface SessionResponse {
  user: AuthUser;
  permissions: Record<string, boolean>;
  accessToken?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  permissions: Record<string, boolean>;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** الحساب بكلمة مرور أولية — لا تُتاح أي شاشة قبل تغييرها */
  mustChangePassword: boolean;
  can: (permission: Permission) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [accessToken, setToken] = useState<string | null>(null);

  const session = useQuery<SessionResponse | null>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const data = await apiGet<SessionResponse>('/api/auth/me');
        return data;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const loginMutation = useMutation({
    mutationFn: async (payload: { email: string; password: string }) =>
      apiPost<SessionResponse>('/api/auth/login', payload),
    onSuccess: (data) => {
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        setToken(data.accessToken);
      }
      queryClient.setQueryData(['session'], { user: data.user, permissions: data.permissions });
    },
  });

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout', {});
    } catch {
      /* الجلسة قد تكون منتهية أصلاً */
    }
    setAccessToken(null);
    setToken(null);
    queryClient.setQueryData(['session'], null);
    queryClient.clear();
  }, [queryClient]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string, confirmPassword: string) => {
      await apiPost('/api/auth/change-password', { currentPassword, newPassword, confirmPassword });
      setAccessToken(null);
      setToken(null);
      queryClient.setQueryData(['session'], null);
      queryClient.clear();
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(() => {
    const user = session.data?.user ?? null;
    const permissions = session.data?.permissions ?? {};
    return {
      user,
      permissions,
      isLoading: session.isLoading,
      isAuthenticated: Boolean(user) && Boolean(accessToken) === Boolean(accessToken), // الجلسة تُتحقق من الخادم
      mustChangePassword: user?.mustChangePassword === true,
      can: (permission: Permission) => permissions[permission] === true,
      login: async (email: string, password: string) => {
        await loginMutation.mutateAsync({ email, password });
      },
      logout,
      changePassword,
    };
  }, [session.data, session.isLoading, accessToken, loginMutation, logout, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth يجب أن يُستخدم داخل AuthProvider');
  return context;
}

/** إخفاء/تعطيل عنصر حسب الصلاحية (الخادم يبقى حدّ الأمان) */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element | null {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
